/**
 * Support Requests API Client
 *
 * Thin axios wrappers for /api/v1/support-requests/* endpoints.
 * Types mirror the backend Pydantic schemas in app/schemas/support_requests.py.
 */
import { api } from '@claimshield/shared'
import type { PageContext } from '../lib/pageContext'

export type RequestType = 'bug' | 'enhancement'
export type RequestStatus =
  | 'submitted' | 'triaging' | 'in_progress' | 'needs_info' | 'resolved' | 'declined' | 'escalated'

export interface SupportRequest {
  id: string
  request_type: RequestType
  category: string
  description: string
  status: RequestStatus
  priority: 'low' | 'medium' | 'high'
  submitted_by_id: string
  submitted_by_name: string
  page_context: PageContext | null
  diagnosis: string | null
  resolution: string | null
  created_at: string
  updated_at: string
  resolved_at: string | null
}

export interface SupportComment {
  id: string
  support_request_id: string
  author_id: string
  author_name: string
  body: string
  is_internal: boolean
  created_at: string
}

export interface SearchResult {
  type: string          // 'Client' | 'Driver' | 'Applicant'
  id: string
  label: string
  sub: string
  client_id?: string | null
}

export interface ClientOpenBug {
  id: string
  request_type: RequestType
  status: RequestStatus
  priority: 'low' | 'medium' | 'high'
  description: string
  submitted_by_name: string
  related_employee_id: string | null
  related_employee_name: string | null
  created_at: string
}

export interface CreatePayload {
  request_type: RequestType
  description: string
  priority: 'low' | 'medium' | 'high'
  page_context?: PageContext | null
  related_client_id?: string | null
  related_employee_id?: string | null
}

export const supportApi = {
  create: (p: CreatePayload) =>
    api.post<SupportRequest>('/v1/support-requests', p).then((r) => r.data),
  list: (params?: { status?: string }) =>
    api.get<{ items: SupportRequest[]; total: number }>('/v1/support-requests', { params }).then((r) => r.data),
  get: (id: string) =>
    api.get<SupportRequest>(`/v1/support-requests/${id}`).then((r) => r.data),
  listComments: (id: string) =>
    api.get<SupportComment[]>(`/v1/support-requests/${id}/comments`).then((r) => r.data),
  addComment: (id: string, body: string, isInternal = false) =>
    api.post<SupportComment>(`/v1/support-requests/${id}/comments`, { body, is_internal: isInternal }).then((r) => r.data),
  update: (id: string, patch: Record<string, unknown>) =>
    api.patch<SupportRequest>(`/v1/support-requests/${id}`, patch).then((r) => r.data),
  search: (q: string) =>
    api.get<{ data: SearchResult[] }>('/v1/portal/search', { params: { q } }).then((r) => r.data.data ?? []),
  listOpenByClient: (clientId: string) =>
    api.get<{ items: ClientOpenBug[]; total: number }>(`/v1/support-requests/by-client/${clientId}`).then((r) => r.data),
}
